/* istanbul ignore file */
import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import SuperCluster, { ClusterProperties, PointFeature } from 'supercluster'
import dynamic from 'next/dynamic'
import debounce from 'lodash/debounce'
import groupBy from 'lodash/groupBy'
import { useLazyQuery } from '@apollo/client'
import { useRouter } from 'next/router'
import { parseLocation } from 'lib/location'
import {
  Listing,
  SearchListingsQuery,
  SearchListingsQueryVariables,
} from 'graphql/types'
import useMap from 'hooks/useMap'
import useMediaQuery from 'hooks/useMediaQuery'
import useEventListener from 'hooks/useEventListener'
import Map, { Marker, Cluster, Multiple } from 'components/Map'
import Loader from 'components/Loader'
import Typography from 'components/Typography'
import Button from 'components/Button'
import GET_SEARCH_LISTING from 'graphql/queries/searchListings'
import { TQueryParams, getAppliedFilters } from 'lib/search'
import {
  openSearchMapEvent,
  searchMapClusterClickEvent,
  searchMapMarkerClickEvent,
  searchMapMultipleClickEvent,
  searchMapZoomEvent,
  searchResultEvent,
  searchMapNotFound,
  searchMapNotFoundNotification,
  searchMapNotFoundNotificationClose,
  searchMapNotFoundNotificationFilterButton,
} from '../../events'
import classNames from 'classnames'
import styles from './ListingsMap.module.scss'

const Modal = dynamic(() => import('components/Modal'))
const ListingDetails = dynamic(() => import('../ListingDetails'))
const MultipleListingDetails = dynamic(
  () => import('../MultipleListingDetails'),
)

export type TProps = {
  initialCenter: google.maps.LatLngLiteral
  initialZoom?: number
  variablesContext: SearchListingsQueryVariables
  onActionClick?: () => void
  filtersCount?: number
}

type TCluster = {
  listing: Listing
  isMultiple: boolean
  multipleSize: number
  multipleListings: Listing[]
} & ClusterProperties

type Bounds = [number, number, number, number]

export type TMapLocation = {
  bounds: Bounds
  center: google.maps.LatLngLiteral
  zoom: number
}

export const getDefaultLocation = ({
  center,
  zoom,
}: Pick<TMapLocation, 'center' | 'zoom'>) => {
  const location = { ...center, zoom }
  if (typeof window === 'undefined') return location
  if (!window.location.hash) return location

  try {
    const [lat, lng, hashZoom] = window.location.hash.slice(2).split(',')
    const latAsNumber = Number(lat)
    const lngAsNumber = Number(lng)

    if (!Number.isNaN(latAsNumber) && !Number.isNaN(lngAsNumber))
      return { lat: latAsNumber, lng: lngAsNumber, zoom: Number(hashZoom) }

    return location
  } catch {
    return location
  }
}

const ListingsMap: FC<TProps> = ({
  initialCenter,
  initialZoom = 15,
  variablesContext,
  onActionClick,
  filtersCount,
}) => {
  const router = useRouter()
  const isDeskResolution = useMediaQuery('(min-width: 768px)')
  const mapRef = useRef<google.maps.Map | null>()
  const {
    lat: defaultLat,
    lng: defaultLng,
    zoom: defaultZoom,
  } = getDefaultLocation({ center: initialCenter, zoom: initialZoom })
  const center = { lat: defaultLat, lng: defaultLng }

  const [bounds, setBounds] = useState<Bounds | number[]>([])
  const [zoom, setZoom] = useState(defaultZoom)
  const [selectedMarker, setSelectedMarker] = useState<Listing | null>(null)
  const [selectedMultiple, setSelectedMultiple] = useState<Listing[] | null>(
    null,
  )
  const [emptyModal, { setAll: setEmptyModal }] = useMap({
    isMounted: false,
    isVisible: false,
  })
  const [modalMultiple, { set, setAll }] = useMap({
    isMounted: false,
    isVisible: false,
  })

  const [getSearchMap, { data, loading, previousData }] = useLazyQuery<
    SearchListingsQuery,
    SearchListingsQueryVariables
  >(GET_SEARCH_LISTING, {
    ssr: false,
    onCompleted: (result) => {
      const totalCount = result?.searchListings?.totalCount || 0

      if (totalCount > 0) {
        const filters = getAppliedFilters({
          queryParams: variablesContext as TQueryParams,
        }) as TQueryParams

        setEmptyModal({
          isMounted: emptyModal.isMounted,
          isVisible: false,
        })

        searchResultEvent({
          city: filters.citiesSlug,
          region: filters.stateSlug,
          filters,
          filtersCount,
          listings: result?.searchListings?.listings as Listing[],
          itemsCount: totalCount,
          itemsDisplayed: totalCount,
          pagetype: 'searchresults',
          isMap: true,
        })
      } else {
        searchMapNotFound(filtersCount)
        setEmptyModal({
          isMounted: true,
          isVisible: true,
        })
        searchMapNotFoundNotification(filtersCount)
      }
    },
  })

  const updateHashLocation = ({
    center: _center,
    zoom: _zoom,
  }: Pick<TMapLocation, 'center' | 'zoom'>) => {
    void router.replace({
      hash: `@${_center.lat},${_center.lng},${_zoom}`,
    })
  }

  const listings = useMemo(
    () =>
      data?.searchListings?.listings || previousData?.searchListings?.listings,
    [data?.searchListings?.listings, previousData?.searchListings?.listings],
  ) as Listing[]

  const groupdListings = groupBy(listings, (item) => {
    const latLng = `${item.address?.lat as unknown as string}/${
      item?.address?.lng as unknown as string
    }`
    return latLng
  })

  const groupedToFeature =
    Object.entries(groupdListings).map(([, value]) => {
      return {
        type: 'Feature',
        properties: {
          cluster: false,
          listing: value[0],
          isMultiple: value.length > 1,
          multipleSize: value.length,
          multipleListings: value,
        },
        geometry: {
          type: 'Point',
          coordinates: [value[0].address?.lng, value[0].address?.lat],
        },
      }
    }) || []

  const clusters = new SuperCluster({
    radius: 100,
    minPoints: 4,
  }).load(groupedToFeature as PointFeature<TCluster>[])

  const getClusters = useMemo(
    () => clusters.getClusters(bounds as unknown as Bounds, zoom),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groupedToFeature],
  )

  const handleChange = useCallback(
    debounce(
      ({ bounds: _bounds, center: _center, zoom: _zoom }: TMapLocation) => {
        updateHashLocation({ center: _center, zoom: _zoom })
        setZoom(_zoom)
        setBounds(_bounds)
      },
      500,
    ),
    [JSON.stringify(bounds)],
  )

  const onMarkerClick = useCallback(
    (listing: Listing) => {
      if (selectedMarker?.id === listing.id) {
        setSelectedMarker(null)
      } else {
        setSelectedMarker(listing)
      }
    },
    [selectedMarker],
  )

  const handleCloseMultiple = () => {
    setSelectedMultiple(null)
    set('isVisible', false)
  }

  const handleEscapeToCloseDetails = (event: Event) => {
    if ((event as KeyboardEvent).key === 'Escape' && selectedMarker) {
      setSelectedMarker(null)
    }
  }

  useEventListener({
    type: 'keydown',
    listener: handleEscapeToCloseDetails,
  })

  useEffect(() => {
    const { lat: parsedLat, lng: parsedLng } = parseLocation(
      router.query.lc as string,
    )
    let newCoordinates = initialCenter

    if (parsedLat && parsedLng) {
      newCoordinates = { lat: Number(parsedLat), lng: Number(parsedLng) }
    }
    if (mapRef.current) {
      mapRef.current.panTo(newCoordinates)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query.lc, JSON.stringify(initialCenter)])

  useEffect(() => {
    if (bounds.length && variablesContext.isMap) {
      void getSearchMap({
        variables: {
          ...variablesContext,
          filters: {
            ...variablesContext.filters,
            locationAddress: null,
            location: {
              boundingBox: bounds,
            },
          },
          pagination: {
            from: 0,
            size: 10000,
          },
          isMap: true,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(bounds), JSON.stringify(variablesContext)])

  useEffect(() => {
    if (selectedMarker) setSelectedMarker(null)
  }, [JSON.stringify(variablesContext)])

  useEffect(() => {
    searchMapZoomEvent(zoom)
  }, [zoom])

  useEffect(() => {
    openSearchMapEvent(filtersCount)
  }, [])

  return (
    <div className={styles.ecMapContainer} data-testid="ecListingsMap">
      {loading && !selectedMarker && (
        <div className={styles.ecSearchMapLoading}>
          <Loader />
        </div>
      )}
      <Map
        center={center}
        zoom={zoom}
        onLoadedMap={(map) => {
          mapRef.current = map
        }}
        zoomControl={isDeskResolution}
        onIdle={(props) => {
          const ne = props
            .getBounds()
            ?.getNorthEast()
            .toJSON() as google.maps.LatLngLiteral
          const sw = props
            .getBounds()
            ?.getSouthWest()
            .toJSON() as google.maps.LatLngLiteral

          handleChange({
            bounds: [sw?.lng, sw?.lat, ne?.lng, ne?.lat],
            center: props.getCenter()?.toJSON() as google.maps.LatLngLiteral,
            zoom: props.getZoom() as number,
          })
        }}
        onClick={() => {
          setSelectedMarker(null)
        }}
      >
        {getClusters.map((cluster) => {
          const [lng, lat] = cluster.geometry.coordinates
          const {
            cluster: isCluster,
            point_count: pointCount,
            listing,
            isMultiple,
            multipleSize,
            multipleListings,
          } = cluster.properties as TCluster

          if (isCluster) {
            return (
              <Cluster
                key={cluster.id}
                location={{
                  lat,
                  lng,
                }}
                count={pointCount}
                onClick={(map) => {
                  const expansionZoom = Math.max(
                    clusters.getClusterExpansionZoom(cluster.id as number),
                    15,
                  )
                  map.setZoom(expansionZoom)
                  map.panTo({ lat, lng })
                  searchMapClusterClickEvent({ lat, lng, count: pointCount })
                }}
              />
            )
          }

          if (isMultiple) {
            return (
              <Multiple
                key={`multiple_${multipleListings[0].id as string}`}
                location={{
                  lat,
                  lng,
                }}
                count={multipleSize}
                onClick={() => {
                  const listingIds = multipleListings.map(
                    (item) => item.id,
                  ) as string[]
                  setSelectedMultiple(multipleListings)
                  setAll({
                    isMounted: true,
                    isVisible: true,
                  })
                  searchMapMultipleClickEvent({ lat, lng, listingIds })
                }}
              />
            )
          }

          return (
            <Marker
              key={listing.id}
              listing={listing}
              onClick={(e, listingMarker) => {
                e.stopPropagation()
                onMarkerClick(listingMarker)
                const mapHeight = mapRef.current?.getDiv()
                  .offsetHeight as number
                const mapWidth = mapRef.current?.getDiv().offsetWidth as number

                const markerWidth = 65
                const detailsHeight = 400

                const latPerPixel = (bounds[3] - bounds[1]) / mapHeight
                const latOffset = (detailsHeight / 2) * latPerPixel
                const lngPerPixel = (bounds[0] - bounds[2]) / mapWidth
                const lngOffset = (markerWidth / 2) * lngPerPixel

                const coordinates = {
                  lat: lat - latOffset,
                  lng: lng - lngOffset,
                }
                mapRef.current?.panTo(coordinates)
                searchMapMarkerClickEvent(listing.id as string)
              }}
              selected={selectedMarker?.id === listing.id}
            />
          )
        })}
        {selectedMarker && <ListingDetails listing={selectedMarker} />}
      </Map>

      {emptyModal.isMounted ? (
        <div
          className={classNames(styles.ecSearchMapEmpty, {
            [styles.ecSearchMapEmptyClosed]: !emptyModal.isVisible,
          })}
        >
          <Modal
            bottom
            visible={emptyModal.isVisible}
            handleCloseButtonClick={() => {
              searchMapNotFoundNotificationClose()
              setEmptyModal({ isMounted: true, isVisible: false })
            }}
            handleBackgroundClick={() => {
              searchMapNotFoundNotificationClose()
              setEmptyModal({ isMounted: true, isVisible: false })
            }}
          >
            <div className={styles.ecSearchMapEmptyContainer}>
              <Typography bold variant="Title">
                Não encontramos nenhum imóvel nessa região
              </Typography>
              <Typography>
                Você pode mover o mapa ou modificar os filtros para encontrar
                mais imóveis.
              </Typography>
              <Button
                onClick={() => {
                  searchMapNotFoundNotificationFilterButton(filtersCount)
                  if (onActionClick) onActionClick()
                }}
              >
                Filtrar novamente
              </Button>
            </div>
          </Modal>
        </div>
      ) : null}
      {modalMultiple.isMounted &&
        selectedMultiple &&
        selectedMultiple?.length > 0 && (
          <Modal
            header={{
              title: 'Imóveis no mesmo local',
            }}
            bottom
            handleBackgroundClick={handleCloseMultiple}
            handleCloseButtonClick={handleCloseMultiple}
            visible={modalMultiple.isVisible}
          >
            <MultipleListingDetails listings={selectedMultiple} />
          </Modal>
        )}
    </div>
  )
}

export default memo(ListingsMap)
